import { useCallback } from 'react';
import { message } from 'antd';
import { scaleText, scaleImage, scaleGroup, scaleRect } from '../services/ScaleService';
import { SetEditableByDblClick } from '../GraphicEditorHelpers';
import { setMenuPosition } from '../services/MenuService';
import { DEFAULT_IMAGE } from '../../../config';
import { attachTransformerToSingleNode } from '../utils/layerSelectionSync';
import { refreshTransform } from '../components/properties/PropertiesHelper';
import {
  setAttachLinkIconColorAttr,
  setLayerLabelColorAttr,
} from '../utils/attachLayerPartners';
import { getRuntimeFontFamily } from '../services/FontsService';
const Konva = require('konva');

export const useElementOperations = ({
  stage,
  canvasPosition,
  product,
  api,
  layerDiff,
  maxDepth,
  selectedGroup,
  setSelectedGroup,
  setSelectedElementPosition,
  refreshProperties,
  refreshLayersStructure,
  saveHistory,
  moveAttachedSources,
  updateSourceAttachedOffset,
  detachElement,
  destroyDistributedGroup,
}) => {
  const getTopLevelGroup = useCallback((element) => {
    let current = element;
    while (current.parent && current.parent.getAttr('elementType') === 'group') {
      current = current.parent;
    }
    return current;
  }, []);

  const isParentGroupElement = useCallback((element) => {
    return element?.parent?.getAttr('elementType') === 'group';
  }, []);

  const switchScale = useCallback(
    async (target) => {
      switch (target.getAttr('elementType')) {
        case 'text':
          await scaleText(target.findOne('.main'));
          break;
        case 'image':
          scaleImage(target.findOne('.main'));
          break;
        case 'group':
          await scaleGroup(target);
          break;
        case 'rect':
          scaleRect(target);
          break;
        default:
          break;
      }
      const selectedBackground = target.findOne('.background');
      const targetElement = target.hasName('condition') ? target.parent : target;
      const tr = stage?.findOne('Transformer');
      if (tr && targetElement && tr.nodes().includes(targetElement)) {
        refreshTransform(targetElement);
      }
      if (stage) setMenuPosition(stage, targetElement, selectedBackground, layerDiff);
    },
    [stage, layerDiff],
  );

  const handleTransformEnd = useCallback(
    async (e) => {
      await switchScale(e.target);
      e.target.getLayer().draw();
      if (e.target.hasName('target')) {
        await moveAttachedSources(e.target);
      } else if (e.target.hasName('source')) {
        updateSourceAttachedOffset(e.target);
      }
      refreshProperties();
      saveHistory(e.target.getStage());
    },
    [switchScale, moveAttachedSources, updateSourceAttachedOffset, refreshProperties, saveHistory],
  );

  const addText = useCallback(
    (dynamicField, fontFamily, positions) => {
      if (!stage) return;
      const defaultText = dynamicField ? '' : 'Your text here...';
      const defaultFamily = fontFamily || 'Arial';
      const layer = stage.findOne('#canvas');
      const textGroup = new Konva.Group({
        name: 'elementGroup',
        elementType: 'text',
        draggable: true,
        mode: 'fixed',
        x: positions ? positions.x - 55 : canvasPosition.x,
        y: positions ? positions.y - 25 : canvasPosition.y,
      });
      textGroup.on('transformend', handleTransformEnd);
      const rect = new Konva.Rect({
        draggable: false,
        name: 'element background',
        elementType: 'text',
        fill: 'rgba(0,0,0,0)',
        width: 110,
        height: 50,
      });
      textGroup.add(rect);
      const substrate = new Konva.Rect({
        draggable: false,
        name: 'element noSnap substrate',
        elementType: 'text',
        fill: 'rgba(0,0,0,0)',
        width: 110,
        height: 50,
        substrateLeft: 0,
        substrateRight: 0,
        substrateTop: 0,
        substrateBottom: 0,
        substrateToggle: 0,
        perfectToggle: false,
        cornerRadius: [0, 0, 0, 0],
      });
      textGroup.add(substrate);
      const foundFont = api?.fonts?.find(
        (font) => getRuntimeFontFamily(font) === defaultFamily,
      );
      const text = new Konva.Text({
        draggable: false,
        name: 'element main',
        elementType: 'text',
        dynamicField,
        text: dynamicField ? product?.[dynamicField] : defaultText,
        placeholder: defaultText,
        fullText: dynamicField ? product?.[dynamicField] : defaultText,
        fontFamily: defaultFamily,
        fontUrl: foundFont?.font ?? api?.fonts?.[0]?.font ?? '',
        fontSize: 20,
        align: 'center',
        verticalAlign: 'middle',
        maxLines: 3,
        minFontSize: 10,
        maxFontSize: 20,
        cutText: true,
      });
      if (!dynamicField) {
        SetEditableByDblClick(text, stage, () => refreshProperties());
      }
      textGroup.add(text);
      layer.add(textGroup);
      layer.draw();
      scaleText(text);
      refreshLayersStructure();
      saveHistory(stage);
    },
    [stage, canvasPosition, product, api, handleTransformEnd, refreshProperties, refreshLayersStructure, saveHistory],
  );

  const addImage = useCallback(
    (dynamicField, positions) => {
      if (!stage) return;
      const layer = stage.findOne('#canvas');
      const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
      let xPosition = canvasPosition.x;
      let yPosition = canvasPosition.y;
      if (!isSafari && positions) {
        xPosition = positions.x - 100;
        yPosition = positions.y - 100;
      }
      const imgGroup = new Konva.Group({
        name: 'elementGroup',
        elementType: 'image',
        draggable: true,
        x: xPosition,
        y: yPosition,
      });
      imgGroup.on('transformend', handleTransformEnd);
      const rect = new Konva.Rect({
        draggable: false,
        name: 'element background',
        elementType: 'image',
        width: 200,
        height: 200,
        opacity: 0,
      });
      imgGroup.add(rect);
      const imgUrl = dynamicField ? product?.[dynamicField] : DEFAULT_IMAGE;
      const imageObj = new window.Image();
      imageObj.onload = function () {
        const imgNode = new Konva.Image({
          image: imageObj,
          draggable: false,
          name: 'element main',
          elementType: 'image',
          dynamicField,
          placeholder: DEFAULT_IMAGE,
          url: imgUrl,
          mode: 'fit',
          initWidth: imageObj.width,
          initHeight: imageObj.height,
        });
        rect.fillPatternImage(imageObj);
        imgGroup.add(imgNode);
        layer.add(imgGroup);
        layer.draw();
        scaleImage(imgNode);
        refreshLayersStructure();
        saveHistory(stage);
      };
      imageObj.src = imgUrl;
    },
    [stage, canvasPosition, product, handleTransformEnd, refreshLayersStructure, saveHistory],
  );

  const addShape = useCallback(
    (ShapeClass, elementType, shapeProps = {}) => {
      if (!stage) return;
      const layer = stage.findOne('#canvas');
      const shapeGroup = new Konva.Group({
        name: 'elementGroup',
        elementType,
        draggable: true,
        x: canvasPosition.x + (shapeProps.groupOffsetX || 0),
        y: canvasPosition.y + (shapeProps.groupOffsetY || 0),
      });
      shapeGroup.on('transformend', handleTransformEnd);
      const { groupOffsetX, groupOffsetY, ...props } = shapeProps;
      const shape = new ShapeClass({
        draggable: false,
        name: 'element background',
        elementType,
        width: 200,
        height: 200,
        fill: 'red',
        ...props,
      });
      if (shapeProps.autoOffset) {
        const size = shape.getClientRect();
        shape.offset({ x: size.x, y: size.y });
      }
      shapeGroup.add(shape);
      layer.add(shapeGroup);
      refreshLayersStructure();
      saveHistory(stage);
    },
    [stage, canvasPosition, handleTransformEnd, refreshLayersStructure, saveHistory],
  );

  const addRect = useCallback(() => addShape(Konva.Rect, 'rect'), [addShape]);

  const addStar = useCallback(
    () =>
      addShape(Konva.Star, 'star', {
        numPoints: 5,
        innerRadius: 50,
        outerRadius: 100,
        offset: { x: -100, y: -100 },
      }),
    [addShape],
  );

  const addPolygon = useCallback(
    () => addShape(Konva.RegularPolygon, 'polygon', { sides: 7, radius: 100, autoOffset: true }),
    [addShape],
  );

  const addEllipse = useCallback(
    () =>
      addShape(Konva.Ellipse, 'ellipse', { offset: { x: -100, y: -100 } }),
    [addShape],
  );

  const addLine = useCallback(
    () =>
      addShape(Konva.Line, 'line', {
        fill: 'black',
        stroke: 'black',
        points: [0, 0, 200, 0],
        groupOffsetX: 100,
        groupOffsetY: 100,
      }),
    [addShape],
  );

  const createGroup = useCallback(() => {
    if (!stage) return;
    const tr = stage.findOne('Transformer');
    let nodes = tr.nodes();
    if (!nodes.length && selectedGroup) {
      nodes = selectedGroup instanceof Konva.Transformer ? selectedGroup.nodes() : [selectedGroup];
      if (nodes.length) tr.nodes(nodes);
    }
    if (!nodes.length) {
      message.error('Nothing to group');
      return;
    }

    let layer, outerDepth = 1;
    if (new Set(nodes.map((n) => n.parent._id)).size > 1) {
      for (const element of nodes) {
        let elementParent = element.parent, depth = 0;
        while (depth < maxDepth) {
          if (elementParent?.getAttr('elementType') === 'group') {
            message.error("You can't group elements from side groups!");
            return;
          } else if (!elementParent) break;
          elementParent = elementParent.parent;
          depth++;
        }
      }
      layer = stage.findOne('#canvas');
    } else {
      let elementParent = nodes[0].parent;
      while (elementParent.getAttr('elementType') === 'group') {
        elementParent = elementParent.parent;
        outerDepth++;
      }
      layer = nodes[0].parent;
    }

    const hasGroupAtMaxDepth = (elements, depth) => {
      if (depth >= maxDepth) return elements.length > 0;
      return elements.some((el) =>
        hasGroupAtMaxDepth(
          el.children.filter((c) => c.getAttr('elementType') === 'group'),
          depth + 1,
        ),
      );
    };

    if (outerDepth > maxDepth) {
      message.error("You can't nest more than 3 times!");
      return;
    }
    if (hasGroupAtMaxDepth(nodes.filter((o) => o.getAttr('elementType') === 'group'), outerDepth)) {
      message.error("You can't nest more than 3 times!");
      return;
    }

    const nodesFromDifferentParents = new Set(nodes.map((n) => n.parent._id)).size > 1;
    let minPosition, positionsInLayer;

    if (nodesFromDifferentParents) {
      const layerAbs = layer.absolutePosition();
      positionsInLayer = nodes.map((n) => {
        const abs = n.absolutePosition();
        return { x: abs.x - layerAbs.x, y: abs.y - layerAbs.y };
      });
      minPosition = {
        x: Math.min(...positionsInLayer.map((p) => p.x)),
        y: Math.min(...positionsInLayer.map((p) => p.y)),
      };
    } else {
      minPosition = {
        x: Math.min(...nodes.map((o) => o.x())),
        y: Math.min(...nodes.map((o) => o.y())),
      };
    }

    const group = new Konva.Group({
      name: 'elementGroup',
      elementType: 'group',
      draggable: true,
      ...minPosition,
    });
    group.on('transformend', handleTransformEnd);
    layer.add(group);
    const sortedNodes = [...nodes].sort((a, b) => a.zIndex() - b.zIndex());
    for (let i = 0; i < sortedNodes.length; i++) {
      const el = sortedNodes[i];
      el.draggable(false);
      if (nodesFromDifferentParents) {
        const posInLayer = positionsInLayer[nodes.indexOf(el)];
        el.position({ x: posInLayer.x - minPosition.x, y: posInLayer.y - minPosition.y });
      } else {
        el.position({ x: el.x() - minPosition.x, y: el.y() - minPosition.y });
      }
      el.moveTo(group);
    }
    attachTransformerToSingleNode(tr, group);
    setSelectedGroup(group);
    saveHistory(stage);
    return group;
  }, [stage, maxDepth, handleTransformEnd, setSelectedGroup, saveHistory, selectedGroup]);

  const destroyGroup = useCallback(
    (group) => {
      if (!stage) return;
      const tr = stage.findOne('Transformer');
      if (group.hasName('distributed')) destroyDistributedGroup(group);
      if (group.hasName('source')) detachElement(group);
      const scaleX = group.scaleX() ?? 1;
      const scaleY = group.scaleY() ?? 1;
      const children = [...group.children];
      children.forEach((t) => {
        if (t.hasName('source')) detachElement(t);
        t.draggable(!isParentGroupElement(group));
        t.position({ x: t.x() * scaleX + group.x(), y: t.y() * scaleY + group.y() });
        if (scaleX !== 1 || scaleY !== 1) {
          t.scale({ x: (t.scaleX() ?? 1) * scaleX, y: (t.scaleY() ?? 1) * scaleY });
        }
        t.moveTo(group.parent);
      });
      if (tr.nodes().includes(group)) tr.nodes([]);
      setSelectedGroup(null);
      group.destroy();
      refreshLayersStructure();
      saveHistory(stage);
    },
    [stage, isParentGroupElement, setSelectedGroup, refreshLayersStructure, saveHistory, detachElement, destroyDistributedGroup],
  );

  const deleteElement = useCallback(
    (element, isHistory = false) => {
      if (!element || !stage) return;
      const parentElement = element?.parent;
      const tr = stage.findOne('Transformer');

      if (element instanceof Konva.Transformer) {
        const elems = tr.nodes();
        tr.nodes([]);
        elems.forEach((elem) => elem.destroy());
      } else if (tr.nodes().includes(element)) {
        tr.nodes([]);
        element.destroy();
      } else {
        element.destroy();
        refreshProperties();
      }

      let emptyParent = parentElement;
      while (emptyParent?.attrs.elementType === 'group' && emptyParent.children.length === 0) {
        const nextParent = emptyParent.parent;
        emptyParent.destroy();
        emptyParent = nextParent;
      }

      setSelectedGroup(null);
      refreshLayersStructure();
      if (!isHistory) saveHistory(stage);

      const menuNode = document.getElementById('menu');
      if (menuNode) menuNode.style.display = 'none';
    },
    [stage, setSelectedGroup, refreshProperties, refreshLayersStructure, saveHistory],
  );

  const copySingleElement = useCallback(
    (element, transformer) => {
      const parent = element.getParent();
      const newElement = element.clone();
      newElement.x(newElement.x() + 50).y(newElement.y() + 50);
      newElement.setAttr('elementName', '');
      setLayerLabelColorAttr(newElement, '');
      setAttachLinkIconColorAttr(newElement, '');

      if (newElement.getAttr('elementType') === 'group') {
        newElement.on('transformend', handleTransformEnd);
      }

      const fixClonedImages = (original, cloned) => {
        const origList = original.find ? original.find('Image') : [];
        const clonedList = cloned.find ? cloned.find('Image') : [];
        if (original.className === 'Image') origList.push(original);
        if (cloned.className === 'Image') clonedList.push(cloned);
        for (const clonedImg of clonedList) {
          const matchOrig = origList.find((o) => o.getAttr('name') === clonedImg.getAttr('name'));
          if (matchOrig?.image?.()) {
            clonedImg.image(matchOrig.image());
          } else {
            const url = clonedImg.getAttr('url') || clonedImg.getAttr('placeholder');
            if (url) {
              const fresh = new window.Image();
              fresh.onload = () => {
                clonedImg.image(fresh);
                clonedImg.getLayer()?.batchDraw();
              };
              fresh.src = url;
            }
          }
        }
      };
      fixClonedImages(element, newElement);

      parent.add(newElement);

      const newElementsText = newElement.children?.find((item) => item instanceof Konva.Text);
      if (newElementsText) {
        const isDynamicText = newElementsText.getAttr('dynamicField');
        if (!isDynamicText) {
          SetEditableByDblClick(newElementsText, stage, () => refreshProperties());
        }
      }

      return newElement;
    },
    [stage, handleTransformEnd, refreshProperties],
  );

  const copyElement = useCallback(
    (element) => {
      if (!element || !stage) return;
      const transformer = stage.findOne('Transformer');

      const isMultiSelect = element instanceof Konva.Transformer && transformer?.nodes()?.length > 1;

      if (isMultiSelect) {
        const originals = transformer.nodes().slice();
        const newElements = [];
        for (const orig of originals) {
          newElements.push(copySingleElement(orig, transformer));
        }
        const layer = originals[0]?.getLayer();
        if (layer) layer.draw();
        transformer.nodes(newElements);
        setSelectedGroup(transformer);
      } else {
        const target = isMultiSelect ? null : element;
        if (!target) return;
        const newElement = copySingleElement(target, transformer);
        target.getLayer()?.draw();
        setSelectedGroup(newElement);
        attachTransformerToSingleNode(transformer, newElement);
      }

      for (let text of stage.find('Text')) {
        text.off('dblclick dbltap');
        text.setAttr('fontStyle', 'normal');
      }
      for (let text of stage.find('Text')) {
        const isDT = text.getAttr('dynamicField');
        if (!isDT) {
          SetEditableByDblClick(text, stage, () => refreshProperties());
        }
      }

      refreshLayersStructure();
      saveHistory(stage);
    },
    [stage, copySingleElement, setSelectedGroup, refreshProperties, refreshLayersStructure, saveHistory],
  );

  const hideElement = useCallback(
    (element) => {
      if (!stage) return;
      const opacity = element.opacity();
      const transformer = stage.findOne('Transformer');
      element.opacity(opacity ? 0 : 1);
      setSelectedGroup(null);
      if (transformer) transformer.nodes([]);
      refreshLayersStructure();
      saveHistory(stage);
    },
    [stage, setSelectedGroup, refreshLayersStructure, saveHistory],
  );

  return {
    getTopLevelGroup,
    isParentGroupElement,
    switchScale,
    handleTransformEnd,
    addText,
    addImage,
    addRect,
    addStar,
    addPolygon,
    addEllipse,
    addLine,
    createGroup,
    destroyGroup,
    deleteElement,
    copyElement,
    hideElement,
  };
};
